import withWidth from '@material-ui/core/withWidth';
import ArrowIcon from '@material-ui/icons/KeyboardArrowDown';
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import InputAdornment from '@material-ui/core/InputAdornment';
import Tooltip from '@material-ui/core/Tooltip';
import ClearIcon from '@material-ui/icons/Clear';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import DateFnsUtils from '@date-io/date-fns';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import PropTypes from 'prop-types';
import React from 'react';
import BasicReadField from './BasicReadField';
import CancelButton from './CancelButton';
import PrimaryButton from './PrimaryButton';
import { globalStyles } from './style/gstheme';

const styles = theme => {
    return Object.assign({
        dateLabel: {
            fontSize: 12,
            color: theme.palette.primary.main,
            marginBottom: 2
        },
        dateActions: {
            backgroundColor: '#eeeeee',
            borderBottomLeftRadius: 4,
            borderBottomRightRadiius: 4,
            clear: 'both',
            padding: 16,
            textAlign: 'right'
        },
        picker: {
            boxShadow: '0px 5px 5px -3px rgb(0 0 0 / 20%), 0px 8px 10px 1px rgb(0 0 0 / 14%), 0px 3px 14px 2px rgb(0 0 0 / 12%)',
            zIndex: 10,
            position: 'absolute',
            marginTop: 2,
            width: 804,
            backgroundColor: 'white',
            borderRadius: 4
        },
        options: {
            float: 'left',
            padding: 12,
            width: 120,
            borderRight: 'solid 2px #cccccc',
            marginTop: 6,
            marginBottom: 6,
            height: 320
        },
        menuEntry: {
            borderRadius: 4,
            fontSize: theme.typography.bodyFontSize,
            color: theme.palette.primary.main,
            padding: 12,
            '&:hover': {
                backgroundColor: '#eeeeee',
                cursor: 'pointer'
            },
            '&:focus-visible': {
                outline: 'dashed 2px black'
            }
        },
        rangeEnd: {
            fontSize: 14,
            width: 40,
            height: 26,
            paddingTop: 10,
            textAlign: 'center',
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            borderRadius: 4,
            '&:hover': {
                cursor: 'pointer'
            }
        },
        inRange: {
            fontSize: 14,
            width: 40,
            height: 26,
            paddingTop: 10,
            textAlign: 'center',
            backgroundColor: '#eeeeee',
            color: theme.palette.primary.main,
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: '#cccccc'
            }
        },
        inRangeNotMonth: {
            fontSize: 14,
            width: 40,
            height: 26,
            paddingTop: 10,
            textAlign: 'center',
            backgroundColor: '#f8f8f8',
            color: '#9e9e9e'
        },
        standardDay: {
            fontSize: 14,
            width: 40,
            height: 26,
            paddingTop: 10,
            textAlign: 'center',
            color: theme.palette.primary.main,
            borderRadius: 4,
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: '#cccccc'
            }
        },
        disabledDay: {
            fontSize: 14,
            width: 40,
            height: 26,
            paddingTop: 10,
            textAlign: 'center',
            color: '#9e9e9e',
            borderRadius: 4
        }
    }, globalStyles);
};

const options = [
    {
        value: "today",
        text: "Today",
        startDate: 0
    },
    {
        value: "last7",
        text: "Last 7 Days",
        startDate: -6
    },
    {
        value: "last30",
        text: "Last 30 Days",
        startDate: -29
    }
];

class DateSelector extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            startDate: props.startDate ? new Date(props.startDate) : new Date(),
            endDate: props.endDate ? new Date(props.endDate) : new Date(),
            selectedStartDate: props.startDate ? new Date(props.startDate) : null,
            selectedEndDate: props.endDate ? new Date(props.endDate) : null,
            visible: false
        };
    }

    handleStartDateChange = (date) => {
        this.setState({ startDate: date });
        if (date.getTime() > this.state.endDate.getTime()) {
            this.setState({ endDate: date });
        }
    };

    handleEndDateChange = (date) => {
        this.setState({ endDate: date });
    };

    showMenu = () => this.setState({ visible: !this.state.visible });

    cancelDate = () => {
        this.setState({ visible: false });
    };

    setRange = (days) => {
        const date = new Date().getTime();
        const newDate = date + (1000 * 60 * 60 * 24 * days);
        this.setState({
            startDate: new Date(newDate),
            endDate: new Date()
        });
    };

    clearDates = () => {
        this.setState({
            selectedStartDate: null,
            selectedEndDate: null,
            visible: false
        });
        this.props.onSetDate();
    };

    applyDate = () => {
        this.setState({
            selectedStartDate: this.state.startDate,
            selectedEndDate: this.state.endDate,
            visible: false
        });
        const startISOdate = this.state.startDate.toISOString().substring(0, 10);
        const endISOdate = this.state.endDate.toISOString().substring(0, 10);
        this.props.onSetDate(startISOdate, endISOdate);
    };

    renderWrappedWeekDay = (isStart) => (date, selectedDate, dayInCurrentMonth) => {

        const dayDate = new Date(date);
        const ms = dayDate.getTime();

        const renderDate = dayDate.getDate();
        const renderMonth = dayDate.getMonth();
        const renderYear = dayDate.getFullYear();

        const startDate = this.state.startDate.getDate();
        const startMonth = this.state.startDate.getMonth();
        const startYear = this.state.startDate.getFullYear();

        const endDate = this.state.endDate.getDate();
        const endMonth = this.state.endDate.getMonth();
        const endYear = this.state.endDate.getFullYear();

        const isInRange = ms > this.state.startDate.getTime() && ms < this.state.endDate.getTime();
        const isStartDay = isStart && startDate === renderDate && startMonth === renderMonth && startYear === renderYear;
        const isEndDay = !isStart && endDate === renderDate && endMonth === renderMonth && endYear === renderYear;

        const disabled = ms > (new Date()).getTime() || (!isStart && ms < this.state.startDate.getTime());

        const { classes } = this.props;

        return (
            <div
                data-test={"date-cell-" + renderYear + "-" + renderMonth + "-" + renderDate}
                data-cell={
                    isStartDay || isEndDay ?
                        "rangeEnd"
                        :
                        isInRange ?
                            dayInCurrentMonth ?
                                "inRange"
                                :
                                "inRangeNotMonth"
                            :
                            dayInCurrentMonth && !disabled ?
                                "standardDay"
                                :
                                "disabledDay"
                }
                className={
                    isStartDay || isEndDay ?
                        classes.rangeEnd
                        :
                        isInRange ?
                            dayInCurrentMonth ?
                                classes.inRange
                                :
                                classes.inRangeNotMonth
                            :
                            dayInCurrentMonth && !disabled ?
                                classes.standardDay
                                :
                                classes.disabledDay
                }>
                {renderDate}
            </ div>
        );
    };

    render() {

        const { classes, dataTest, width } = this.props;
        const { selectedStartDate, selectedEndDate, visible } = this.state;

        const startDate = this.state.startDate.getDate();
        const startMonth = this.state.startDate.toLocaleString('default', { month: 'long' });
        const startYear = this.state.startDate.getFullYear();

        const endDate = this.state.endDate.getDate();
        const endMonth = this.state.endDate.toLocaleString('default', { month: 'long' });
        const endYear = this.state.endDate.getFullYear();

        let dateText = "All history";
        if (selectedStartDate) {
            const sDate = selectedStartDate.getDate();
            const sMonth = selectedStartDate.toLocaleString('default', { month: 'long' });
            const sYear = selectedStartDate.getFullYear();

            const eDate = selectedEndDate.getDate();
            const eMonth = selectedEndDate.toLocaleString('default', { month: 'long' });
            const eYear = selectedEndDate.getFullYear();

            dateText = sDate + " " + sMonth + " " + sYear + " – " + eDate + " " + eMonth + " " + eYear;
        }

        return (
            <div data-test={dataTest}>
                <div>
                    <Typography id="date-range" className={classes.dateLabel}>Date Range</Typography>
                    <TextField
                        value={dateText}
                        size="small"
                        variant="outlined"
                        fullWidth={true}
                        onKeyDown={(ev) => {
                            if (ev.key === "Enter") {
                                ev.preventDefault();
                                ev.stopPropagation();
                                this.showMenu();
                            }
                        }}
                        onClick={this.showMenu}
                        style={{ marginTop: '0px' }}
                        InputProps={{
                            inputProps: {
                                "data-test": dataTest + "-input",
                                "aria-labelledby": "date-range"
                            },
                            endAdornment:
                                <InputAdornment position="end">
                                    <Tooltip title={selectedStartDate ?
                                        "Clear dates"
                                        :
                                        visible ?
                                            "Hide date selection"
                                            :
                                            "Show date selection"}>
                                        <span style={{ borderLeft: 'solid 1px #9e9e9e', paddingTop: 3 }}>
                                            {selectedStartDate ?
                                                <ClearIcon style={{ marginLeft: 2, paddingRight: 2 }} fontSize="small" data-test="clear" className={classes.inputIconHighlighted}
                                                    onClick={(e) => {
                                                        this.clearDates();
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                    }} />
                                                :
                                                visible ?
                                                    <ArrowUpIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} data-test="hide-dates" />
                                                    :
                                                    <ArrowIcon style={{ color: 'rgba(0, 0, 0, 0.54)' }} data-test="show-dates" />
                                            }
                                        </span>
                                    </Tooltip>
                                </InputAdornment>
                            ,
                            className: classes.inputLabelDrop,
                            classes: {
                                input: classes.inputLabelDrop
                            },
                            readOnly: true
                        }} />
                    {visible &&
                        <ClickAwayListener onClickAway={this.showMenu} >
                            <div
                                role="presentation"
                                className={classes.picker}
                                style={{ ...(width === "xs" || width === "sm" || width === "md") && { width: '660px' } }}>
                                {width !== "xs" && width !== "sm" && width !== "md" &&
                                    <div className={classes.options}>
                                        {
                                            options.map((entry, index) => {
                                                return <div
                                                    tabIndex={0}
                                                    data-test={entry.value}
                                                    key={index}
                                                    onKeyDown={(ev) => {
                                                        if (ev.key === "Enter") {
                                                            ev.preventDefault();
                                                            ev.stopPropagation();
                                                            this.setRange(entry.startDate);
                                                        }
                                                    }}
                                                    onClick={() => this.setRange(entry.startDate)}
                                                    className={classes.menuEntry}>
                                                    {entry.text}
                                                </div>;
                                            })
                                        }
                                    </div>
                                }
                                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                    <div data-test="end-date-picker" style={{ float: 'right', height: 300, marginRight: 4 }}>
                                        <DatePicker
                                            orientation="landscape"
                                            disableFuture
                                            disableToolbar
                                            variant="static"
                                            openTo="date"
                                            minDate={this.state.startDate}
                                            value={this.state.endDate}
                                            renderDay={this.renderWrappedWeekDay(false)}
                                            onChange={this.handleEndDateChange}
                                        />
                                    </div>
                                    <div data-test="start-date-picker" style={{ float: 'right', height: 300 }}>
                                        <DatePicker
                                            orientation="landscape"
                                            disableFuture
                                            disableToolbar
                                            variant="static"
                                            openTo="date"
                                            value={this.state.startDate}
                                            renderDay={this.renderWrappedWeekDay(true)}
                                            onChange={this.handleStartDateChange}
                                        />
                                    </div>
                                </MuiPickersUtilsProvider>
                                <div style={{ float: 'right', marginTop: 10, marginRight: 20, marginBottom: 20 }}>
                                    <div style={{ width: 280, display: 'inline-block', marginRight: 20 }}>
                                        <BasicReadField dataTest="date-from" fullWidth text={startDate + " " + startMonth + " " + startYear} />
                                    </div>
                                    <div style={{ width: 20, transform: 'translateY(10px)', display: 'inline-block', marginRight: 10 }}>–</div>
                                    <div style={{ width: 280, display: 'inline-block' }}>
                                        <BasicReadField dataTest="date-to" fullWidth text={endDate + " " + endMonth + " " + endYear} />
                                    </div>
                                </div>
                                <div className={classes.dateActions}>
                                    <span style={{ marginRight: 12 }}>
                                        <CancelButton onClick={this.cancelDate} />
                                    </span>
                                    <PrimaryButton onClick={this.applyDate}>Apply</PrimaryButton>
                                </div>
                            </div>
                        </ClickAwayListener>
                    }
                </div>

            </div>
        );
    }
}

DateSelector.defaultProps = {
    dataTest: "date-selector"
};

DateSelector.propTypes = {
    width: PropTypes.string,
    onSetDate: PropTypes.func.isRequired,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    dataTest: PropTypes.string,
    classes: PropTypes.object
};

export default withWidth()(withStyles(styles)(DateSelector));